import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { ExternalLink, ImageOff, Video, X } from 'lucide-react';
import { apiMediaSrc, type StrategyExemplarRecord, type StrategyExemplarStrategy } from '@/services/api';
import { ClassBadge, Chip, Field, asList, formatDays, toText } from './shared';

/** The ad's image (or video); a placeholder when there is none or it fails to load. */
export function ExemplarMedia({
  record,
  className,
  fit = 'cover',
  controls = false,
  thumb = false,
}: {
  record: StrategyExemplarRecord;
  className?: string;
  fit?: 'cover' | 'contain';
  controls?: boolean;
  /** Load the small JPEG thumbnail (grids); the lightbox loads the original. */
  thumb?: boolean;
}) {
  const isVideo = !!record.media_mime?.startsWith('video/');
  const src = apiMediaSrc(thumb && !isVideo ? record.thumb_url ?? record.media_url : record.media_url);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return (
      <div className={clsx('flex flex-col items-center justify-center gap-1 bg-slate-100 text-slate-400 text-[11px]', className)}>
        <ImageOff size={18} />
        {record.missing ? 'not in exemplar index' : failed ? 'image unavailable' : 'no media'}
      </div>
    );
  }
  const fitCls = fit === 'cover' ? 'object-cover' : 'object-contain';
  if (isVideo) {
    return (
      <video
        src={src}
        className={clsx(fitCls, 'bg-slate-900', className)}
        controls={controls}
        muted
        playsInline
        preload="metadata"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <img
      src={src}
      alt={record.advertiser ? `Ad by ${record.advertiser}` : record.ad_key}
      loading="lazy"
      className={clsx(fitCls, 'bg-slate-100', className)}
      onError={() => setFailed(true)}
    />
  );
}

function excerpt(text: string | undefined, n: number): string {
  if (!text) return '';
  return text.length > n ? `${text.slice(0, n).trimEnd()}…` : text;
}

/**
 * One exemplar ad as a card. `onOpen` opens the lightbox; `onOpenItem` (when
 * given) makes the cited_by chips navigate to that item.
 */
export function ExemplarCard({
  record,
  onOpen,
  onOpenItem,
  showLens = true,
}: {
  record: StrategyExemplarRecord;
  onOpen: (r: StrategyExemplarRecord) => void;
  onOpenItem?: (id: string) => void;
  showLens?: boolean;
}) {
  const lens = record.lens || {};
  const days = formatDays(record.days_active);
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col">
      <button type="button" onClick={() => onOpen(record)} className="relative block w-full aspect-square group" title="Open ad">
        <ExemplarMedia record={record} thumb className="w-full h-full" />
        {record.is_video && (
          <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 rounded bg-black/60 text-white text-[10px] px-1.5 py-0.5">
            <Video size={10} /> video
          </span>
        )}
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
      </button>
      <div className="p-2.5 space-y-1.5 text-xs flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-navy-900 truncate" title={record.advertiser || record.ad_key}>
            {record.advertiser || <span className="font-mono text-slate-500">{record.ad_key}</span>}
          </span>
          {record.library_url && (
            <a
              href={record.library_url}
              target="_blank"
              rel="noreferrer"
              className="text-slate-400 hover:text-brand-teal-dark flex-shrink-0"
              title="Open in Meta Ad Library"
            >
              <ExternalLink size={12} />
            </a>
          )}
        </div>
        <div className="flex flex-wrap gap-1 text-[10px] text-slate-500">
          {days && <span>{days} active</span>}
          {record.format && <span>· {record.format}</span>}
          {record.is_active && <span className="text-emerald-600">· live</span>}
          {record.source === 'template_catalog' && <span>· catalog</span>}
        </div>
        {record.copy && <p className="text-slate-600 leading-snug">{excerpt(record.copy, 120)}</p>}
        {showLens && (lens.creative_template_name || lens.layout_archetype || lens.hook) && (
          <div className="text-[11px] text-slate-500 space-y-0.5 border-t border-slate-100 pt-1.5">
            {lens.creative_template_name && <div className="text-slate-700 font-medium">{toText(lens.creative_template_name)}</div>}
            {lens.layout_archetype && <div className="font-mono">{toText(lens.layout_archetype)}</div>}
            {lens.template_recipe && <div className="line-clamp-2">{toText(lens.template_recipe)}</div>}
            {lens.hook && <div className="italic line-clamp-2">“{toText(lens.hook)}”</div>}
          </div>
        )}
        {record.cited_by && record.cited_by.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1 mt-auto">
            {record.cited_by.map((c) => (
              <button
                key={c.id}
                type="button"
                disabled={!onOpenItem}
                onClick={() => onOpenItem?.(c.id)}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-slate-200 text-slate-700 hover:border-brand-teal hover:text-navy-900 max-w-full"
                title={`${c.name} (${c.id})`}
              >
                <ClassBadge entityClass={c.entity_class} />
                <span className="truncate">{c.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function isStrategy(v: unknown): v is StrategyExemplarStrategy {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** Full image, full copy and the full lens for one exemplar ad. */
export function ExemplarLightbox({
  record,
  onClose,
  onOpenItem,
}: {
  record: StrategyExemplarRecord | null;
  onClose: () => void;
  onOpenItem?: (id: string) => void;
}) {
  useEffect(() => {
    if (!record) return;
    // Capture phase on window so Escape closes only the lightbox, not a drawer under it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [record, onClose]);

  if (!record) return null;
  const lens = record.lens || {};
  const strategies = asList(lens.strategies);
  const concerns = asList(lens.buyer_concerns);
  const concepts = asList(lens.concepts);
  const shown = new Set(['creative_template_name', 'layout_archetype', 'template_recipe', 'hook', 'strategies', 'buyer_concerns', 'concepts']);
  const otherLens = Object.entries(lens).filter(([k, v]) => !shown.has(k) && toText(v));

  // Portal: a fixed overlay inside the (transformed) slide-over would be clipped to it.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col md:flex-row overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal
      >
        <div className="md:w-1/2 bg-slate-900 flex items-center justify-center min-h-[240px]">
          <ExemplarMedia record={record} fit="contain" controls className="w-full h-full max-h-[92vh]" />
        </div>
        <div className="md:w-1/2 flex flex-col min-h-0">
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200">
            <div className="min-w-0">
              <div className="text-base font-semibold text-navy-900 truncate">{record.advertiser || 'Unknown advertiser'}</div>
              <div className="text-xs text-slate-500 font-mono truncate">{record.ad_key}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {record.library_url && (
                <a
                  href={record.library_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-slate-300 text-slate-700 hover:border-brand-teal"
                >
                  Ad Library <ExternalLink size={11} />
                </a>
              )}
              <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100" aria-label="Close">
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div className="flex flex-wrap gap-1.5">
              {record.source && <Chip>{record.source}</Chip>}
              {record.format && <Chip>{record.format}</Chip>}
              {formatDays(record.days_active) && <Chip>{formatDays(record.days_active)} active</Chip>}
              {record.is_active != null && <Chip tone={record.is_active ? 'emerald' : 'slate'}>{record.is_active ? 'still running' : 'stopped'}</Chip>}
              {record.is_video && <Chip>video</Chip>}
              {record.n_cards ? <Chip>{record.n_cards} cards</Chip> : null}
              {record.rung && <Chip tone="navy">rung {record.rung}</Chip>}
              {record.missing && <Chip tone="amber">not in exemplar index</Chip>}
            </div>
            {record.copy && (
              <Field label="Ad copy">
                <p className="whitespace-pre-wrap text-slate-700">{record.copy}</p>
              </Field>
            )}
            {lens.creative_template_name && <Field label="Template (lens)">{toText(lens.creative_template_name)}</Field>}
            {lens.layout_archetype && <Field label="Layout archetype"><span className="font-mono text-xs">{toText(lens.layout_archetype)}</span></Field>}
            {lens.template_recipe && <Field label="Template recipe">{toText(lens.template_recipe)}</Field>}
            {lens.hook && <Field label="Hook"><span className="italic">“{toText(lens.hook)}”</span></Field>}
            {strategies.length > 0 && (
              <Field label="Strategies">
                <ul className="space-y-2">
                  {strategies.map((s, i) =>
                    isStrategy(s) ? (
                      <li key={i} className="rounded-lg border border-slate-200 px-3 py-2">
                        <div className="flex items-center gap-2">
                          {s.strategy_type && <Chip tone="navy">{s.strategy_type}</Chip>}
                          <span className="font-medium text-slate-800">{s.name}</span>
                        </div>
                        {s.argument && <p className="text-xs text-slate-600 mt-1">{s.argument}</p>}
                        {s.evidence && <p className="text-[11px] text-slate-500 mt-1 italic">{toText(s.evidence)}</p>}
                      </li>
                    ) : (
                      <li key={i} className="text-sm">{toText(s)}</li>
                    ),
                  )}
                </ul>
              </Field>
            )}
            {concerns.length > 0 && (
              <Field label="Buyer concerns">
                <div className="flex flex-wrap gap-1">{concerns.map((c, i) => <Chip key={i} tone="amber">{toText(c)}</Chip>)}</div>
              </Field>
            )}
            {concepts.length > 0 && (
              <Field label="Concepts">
                <div className="flex flex-wrap gap-1">{concepts.map((c, i) => <Chip key={i}>{toText(c)}</Chip>)}</div>
              </Field>
            )}
            {otherLens.length > 0 && (
              <Field label="Other lens fields">
                <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
                  {otherLens.map(([k, v]) => (
                    <div key={k} className="contents">
                      <dt className="text-slate-500 font-mono">{k}</dt>
                      <dd className="text-slate-700">{toText(v)}</dd>
                    </div>
                  ))}
                </dl>
              </Field>
            )}
            {record.cited_by && record.cited_by.length > 0 && (
              <Field label="Cited by">
                <div className="flex flex-wrap gap-1.5">
                  {record.cited_by.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      disabled={!onOpenItem}
                      onClick={() => {
                        onClose();
                        onOpenItem?.(c.id);
                      }}
                      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-slate-200 hover:border-brand-teal"
                    >
                      <ClassBadge entityClass={c.entity_class} /> {c.name}
                    </button>
                  ))}
                </div>
              </Field>
            )}
            <p className="text-[11px] text-slate-400">Competitor creative: for internal review only — never sent to a generation provider.</p>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
